import { Head, router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { PausedBanner } from "@/components/creator/paused/paused-banner"
import { PausedCta } from "@/components/creator/paused/paused-cta"
import { PausedIdentity } from "@/components/creator/paused/paused-identity"
import {
  PausedRecordCard,
  type PausedRecordData,
} from "@/components/creator/paused/paused-record-card"
import { useCurrentContext } from "@/hooks/use-current-context"
import { CreatorPausedShell } from "@/layouts/app/creator-paused-shell"
import { formatActivePeriod, formatPauseDate } from "@/lib/paused-dates"

/** Server rank shape for the RankPatch mini (`null` when ladder empty). */
interface PausedRankProps {
  name: string
  position: number
  tier_count: number
}

/** Server record shape (ISO dates) — `null` when the read failed. */
interface PausedRecordProps {
  missions_completed: number
  views: number
  badges_count: number
  streak_days: number
  rank: PausedRankProps | null
  joined_at: string | null
  paused_at: string | null
}

export interface CreatorPausedProps {
  brand_name: string
  /** IANA name — pins the active-period dates to the brand's zone (KD-7). */
  brand_timezone: string
  /** `"member_self" | "brand_admin" | null` — drives the source CTA. */
  pause_source: string | null
  /** Brand-paused only: a reactivation request is already on file. */
  reactivation_requested: boolean
  paused_at: string | null
  record: PausedRecordProps | null
  record_error: boolean
}

/**
 * OKS-404 / OKS-561 — Dignified paused member view (`/paused`, §22).
 *
 * Single mobile-first column inside the stripped `CreatorPausedShell`
 * (no bottom-tab nav — a paused membership can reach this screen and the
 * brand message thread only; the server-authoritative
 * `CreatorPaused::RedirectConcern` 303s every gated surface here). Reading
 * order = visual order (UX Spec §2):
 *   1. muted BLUE pause banner (with date; "Admin action ·" prefix when
 *      brand-paused)
 *   2. dimmed identity (0.55 avatar + greyed name)
 *   3. "YOUR RECORD WITH {BRAND}" card — missions · views · badges · streak
 *      + RankPatch-mini footer "Final rank · Active {start}–{end}", or the
 *      rookie/no-record edge, or the error+retry variant
 *   4. source-dependent CTA (reactivate / message + request / request-sent)
 *
 * The 7 UX-Spec states resolve as: paused (default), loading (Inertia visit
 * skeleton), error (`record_error`), success 🎉 (server redirect to
 * `/missions` + toast — never rendered here), rookie edge (`isRookie`),
 * reactivation-requested (`reactivation_requested`), brand-paused
 * (`pause_source === "brand_admin"`).
 *
 * # Brand isolation
 *
 * `brand_name` + every record figure come from the controller scoped to
 * `Current.membership` (single brand). Identity (name) reads from the
 * shared `currentUser` context. No client-side query, no cross-brand leak.
 *
 * # Dates
 *
 * The server ships ISO strings + `brand_timezone`; formatting is
 * locale-aware AND brand-tz-pinned on the client (KD-7).
 */
export default function CreatorPaused({
  brand_name,
  brand_timezone,
  pause_source,
  reactivation_requested,
  paused_at,
  record,
  record_error,
}: CreatorPausedProps) {
  const { t, i18n } = useTranslation()
  const { currentUser } = useCurrentContext()

  const name = currentUser?.name ?? ""
  const brandPaused = pause_source === "brand_admin"
  const pauseDate = formatPauseDate(paused_at, i18n.language, brand_timezone)

  const recordData: PausedRecordData | null = record
    ? {
        missionsCompleted: record.missions_completed,
        views: record.views,
        badgesCount: record.badges_count,
        streakDays: record.streak_days,
        rank: record.rank
          ? {
              name: record.rank.name,
              position: record.rank.position,
              tierCount: record.rank.tier_count,
            }
          : null,
        activePeriod: formatActivePeriod(
          record.joined_at,
          record.paused_at,
          i18n.language,
          brand_timezone,
        ),
        // No-record edge (UX Spec §3): the member has zero contribution on
        // every axis — show the honest "Rookie · Joined {date}" line.
        isRookie:
          record.missions_completed === 0 &&
          record.views === 0 &&
          record.badges_count === 0 &&
          record.streak_days === 0,
        joinedDate: formatPauseDate(
          record.joined_at,
          i18n.language,
          brand_timezone,
        ),
      }
    : null

  return (
    <CreatorPausedShell>
      <Head title={t("crew_paused.page_title")} />

      <div className="mx-auto flex w-full max-w-md flex-col gap-6 px-4 py-6">
        <PausedBanner
          brandName={brand_name}
          pauseDate={pauseDate}
          brandPaused={brandPaused}
        />

        <PausedIdentity
          name={name}
          brandName={brand_name}
          avatarUrl={currentUser?.avatar_url}
        />

        <PausedRecordCard
          brandName={brand_name}
          record={recordData}
          error={record_error}
          onRetry={() => router.reload()}
        />

        <PausedCta
          brandName={brand_name}
          pauseSource={pause_source}
          reactivationRequested={reactivation_requested}
        />
      </div>
    </CreatorPausedShell>
  )
}
